import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Settings, Sparkles, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸' },
  { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺' },
  { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', flag: '🇨🇦' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', flag: '🇦🇺' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', flag: '🇮🇳' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', flag: '🇯🇵' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', flag: '🇨🇳' },
];

export const CurrencyOnboarding = ({ onComplete }) => {
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContinue = async () => {
    if (!selectedCurrency) return;
    setIsSubmitting(true);
    await onComplete(selectedCurrency);
    setIsSubmitting(false);
  };

  const selectedCurrencyData = CURRENCIES.find(c => c.code === selectedCurrency);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-primary/10 flex items-center justify-center p-4" data-testid="currency-onboarding-screen">
      <Card className="w-full max-w-md border-0 shadow-2xl overflow-hidden">
        <CardContent className="p-0">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-primary/20 to-primary/5 p-6 text-center">
            <div className="w-14 h-14 rounded-2xl bg-background shadow-lg flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-7 h-7 text-primary" />
            </div>
            <h1 className="text-xl font-bold mb-1" data-testid="onboarding-title">
              Welcome! Let's get started
            </h1>
            <p className="text-sm text-muted-foreground">
              What currency do you use?
            </p>
          </div>

          <div className="p-6">
            {/* Currency Grid */}
            <div className="grid grid-cols-2 gap-2 mb-5" data-testid="currency-grid">
              {CURRENCIES.map((currency) => (
                <button
                  key={currency.code}
                  data-testid={`currency-option-${currency.code}`}
                  onClick={() => setSelectedCurrency(currency.code)}
                  className={cn(
                    "relative p-3 rounded-xl border-2 transition-all text-left group",
                    selectedCurrency === currency.code
                      ? "border-primary bg-primary/5 shadow-md scale-[1.02]"
                      : "border-transparent bg-muted/30 hover:bg-muted/50 hover:border-muted"
                  )}
                >
                  {selectedCurrency === currency.code && (
                    <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center shadow-sm">
                      <Check className="w-3 h-3 text-primary-foreground" />
                    </div>
                  )}
                  <div className="flex items-center gap-2.5">
                    <span className="text-xl">{currency.flag}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-sm">{currency.code}</span>
                        <span className="text-primary font-bold">{currency.symbol}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground truncate">{currency.name}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Selected currency preview */}
            {selectedCurrency && (
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 mb-5 animate-in fade-in duration-200">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{selectedCurrencyData?.flag}</span>
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      Your amounts will show as <span className="text-primary font-bold">{selectedCurrencyData?.symbol}1,234</span>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Info Note */}
            <div className="flex items-start gap-2.5 text-muted-foreground mb-5 px-1">
              <Settings className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p className="text-xs leading-relaxed">
                Don't worry! You can always change your currency later in <span className="font-medium text-foreground">Settings</span>
              </p>
            </div>

            {/* Continue Button */}
            <Button 
              onClick={handleContinue} 
              className="w-full h-11 text-sm font-semibold gap-2"
              disabled={isSubmitting || !selectedCurrency}
              data-testid="onboarding-continue-button"
            >
              {isSubmitting ? (
                'Setting up your account...'
              ) : selectedCurrency ? (
                <>
                  Get Started
                  <ArrowRight className="w-4 h-4" />
                </>
              ) : (
                'Select a currency to continue'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

